import { useMemo } from 'react';
import { usePastedUrlResolution } from '../../smartLinks/wizards/common/usePastedUrlResolution';
import { serviceLinksFromResolution } from '../../smartLinks/catalog/releaseResolutionLinks';
import { AddReleaseInput } from '../../smartLinks/catalog/useAddRelease';

export type LinkSongState = 'idle' | 'resolving' | 'found' | 'bad';

export interface LinkSong {
  state: LinkSongState;
  /** The add of the song, when the link answered a title. */
  input?: AddReleaseInput;
  imageUrl?: string;
  sourceLabel?: string;
}

/**
 * A pasted song link (design c1, c3): the resolver of the Catalog "Add a release" sheet reads the
 * title, the artist, the cover and the other services. A link that answers no title is a link we
 * cannot read.
 */
export function useLinkSong(url: string | undefined, fallbackArtistName: string): LinkSong {
  const resolution = usePastedUrlResolution(url);

  return useMemo(() => {
    if (url === undefined) {
      return { state: 'idle' };
    }
    if (resolution.isResolving) {
      return { state: 'resolving' };
    }
    if (resolution.title === undefined) {
      return { state: 'bad' };
    }
    const serviceLinks = serviceLinksFromResolution(resolution);
    return {
      state: 'found',
      imageUrl: resolution.imageUrl,
      sourceLabel: resolution.sourceLabel,
      input: {
        title: resolution.title,
        artistName: resolution.artistName ?? fallbackArtistName,
        artworkUrl: resolution.imageUrl,
        // The ISRC of the pasted track (musicapi inspect): the song keeps it (owner answer 2026-10-09).
        isrc: resolution.isrc,
        serviceLinks: Object.keys(serviceLinks).length === 0 ? undefined : serviceLinks,
      },
    };
  }, [fallbackArtistName, resolution, url]);
}
